import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useWhatsApp } from '@/hooks/useWhatsApp';

const messageSchema = z.object({
  phone: z.string().min(10, 'Valid phone number required'),
  message: z.string().min(1, 'Message is required').max(4096, 'Message too long'),
});

const templateSchema = z.object({
  phone: z.string().min(10, 'Valid phone number required'),
  templateName: z.string().min(1, 'Template name is required'),
  templateLanguage: z.string().default('en_US'),
});

type MessageFormData = z.infer<typeof messageSchema>;
type TemplateFormData = z.infer<typeof templateSchema>;

interface SendWhatsAppDialogProps {
  leadId?: string;
  contactId?: string;
  dealId?: string;
  defaultPhone?: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  leadPhone?: string;
  leadName?: string;
  contactPhone?: string;
  contactName?: string;
}

export function SendWhatsAppDialog({ 
  leadId, 
  contactId, 
  dealId, 
  defaultPhone,
  trigger,
  open: controlledOpen,
  onOpenChange,
  leadPhone,
  contactPhone,
  leadName,
  contactName
}: SendWhatsAppDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange! : setInternalOpen;
  const { isSending, sendMessage, sendTemplate } = useWhatsApp();
  
  const phoneToUse = defaultPhone || leadPhone || contactPhone || '';

  const messageForm = useForm<MessageFormData>({
    resolver: zodResolver(messageSchema),
    defaultValues: {
      phone: phoneToUse,
      message: '',
    },
  });

  const templateForm = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      phone: phoneToUse,
      templateName: '',
      templateLanguage: 'en_US',
    },
  });

  const onSendMessage = async (data: MessageFormData) => {
    try {
      await sendMessage({
        to: data.phone,
        message: data.message,
        leadId,
        contactId,
        dealId,
      });
      messageForm.reset();
      setOpen(false);
    } catch (err) {
      // Error handled in hook
    }
  };

  const onSendTemplate = async (data: TemplateFormData) => {
    try {
      await sendTemplate({
        to: data.phone,
        templateName: data.templateName,
        templateLanguage: data.templateLanguage,
        leadId,
        contactId,
        dealId,
      });
      templateForm.reset();
      setOpen(false);
    } catch (err) {
      // Error handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm" className="gap-2">
              <MessageCircle className="h-4 w-4 text-green-500" />
              WhatsApp
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-500" />
            Send WhatsApp Message
          </DialogTitle>
          <DialogDescription>
            Send a text message or use a pre-approved template via WhatsApp Business
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="message" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="message">Text Message</TabsTrigger>
            <TabsTrigger value="template">Template</TabsTrigger>
          </TabsList>

          <TabsContent value="message" className="mt-4">
            <Form {...messageForm}>
              <form onSubmit={messageForm.handleSubmit(onSendMessage)} className="space-y-4">
                <FormField
                  control={messageForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="+1234567890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={messageForm.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Type your message..."
                          className="min-h-[120px] resize-none"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSending} className="gap-2">
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Send Message
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="template" className="mt-4">
            <Form {...templateForm}>
              <form onSubmit={templateForm.handleSubmit(onSendTemplate)} className="space-y-4">
                <FormField
                  control={templateForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="+1234567890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={templateForm.control}
                  name="templateName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template Name</FormLabel>
                      <FormControl>
                        <Input placeholder="hello_world" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={templateForm.control}
                  name="templateLanguage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Language Code</FormLabel>
                      <FormControl>
                        <Input placeholder="en_US" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <p className="text-xs text-muted-foreground">
                  Note: Templates must be pre-approved in your Meta Business account
                </p>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSending} className="gap-2">
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Send Template
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
